import type { FC } from 'react';
import React from 'react';
import VError from 'verror';
import { Text, Box } from '@wix/cli-ui-kit';
import type { IHttpError } from '@wix/http-client';
import { extractHttpError } from '@wix/cli-http-client';

interface RawErrorViewerProps {
  error: unknown;
  occurredAt: Date;
}

export const RawErrorViewer: FC<RawErrorViewerProps> = (props) => {
  const { error, occurredAt } = props;

  return (
    <Box flexDirection="column" paddingTop={1}>
      <Text>----- CLI crash report at {occurredAt.toISOString()}</Text>
      {error instanceof Error ? (
        <FullErrorDetails error={error} />
      ) : (
        <Text>{JSON.stringify(error)}</Text>
      )}
    </Box>
  );
};

const FullErrorDetails: FC<{ error: Error }> = ({ error }) => {
  const httpError = extractHttpError(error);
  const httpErrorInfo = httpError ? getSafeHttpErrorInfo(httpError) : null;

  return (
    <>
      <Text>Error Trace</Text>
      <Text>{VError.fullStack(error)}</Text>
      <Text>Error Info</Text>
      <Text>{toPrettyJson(VError.info(error))}</Text>
      {httpErrorInfo ? (
        <Box flexDirection="column">
          <Text>Request Info</Text>
          <Text>{toPrettyJson(httpErrorInfo)}</Text>
        </Box>
      ) : null}
    </>
  );
};

function getSafeHttpErrorInfo(httpError: IHttpError) {
  return {
    request: {
      baseURL: httpError.config.baseURL,
      url: httpError.config.url,
      method: httpError.config.method,
      data: httpError.config.data,
    },
    response: {
      code: httpError.code,
      message: httpError.message,
      status: httpError.response?.status,
      headers: getSafeHeaders(httpError.response?.headers),
      data: httpError.response?.data,
    },
  };
}

const SAFE_HEADERS = new Set(['x-wix-request-id']);

function getSafeHeaders(headers?: Record<string, unknown>) {
  if (headers) {
    return Object.keys(headers).reduce<Record<string, unknown>>((acc, curr) => {
      if (SAFE_HEADERS.has(curr.toLowerCase())) {
        acc[curr] = headers[curr];
      }
      return acc;
    }, {});
  }
  return headers;
}

function toPrettyJson(data: unknown) {
  return JSON.stringify(data, null, 2);
}
